import { Server, Socket } from 'socket.io';
import { verifyAccessToken } from '../shared/jwt';
import { isTokenBlacklisted, setPresence } from '../shared/redis';
import { prisma } from '../shared/prisma';
import { SocketEvents } from '@ichat/shared-types';
import { messageHandler } from './handlers/message.handler';
import { presenceHandler } from './handlers/presence.handler';
import { typingHandler } from './handlers/typing.handler';
import { webrtcHandler } from './handlers/webrtc.handler';
import { meetingHandler } from './handlers/meeting.handler';

export interface AuthSocket extends Socket {
  userId: string;
  username: string;
  displayName: string;
  permissions: string[];
}

let _io: Server;
export function getIo(): Server {
  if (!_io) throw new Error('Socket.io not initialized');
  return _io;
}

export function registerSocketHandlers(io: Server) {
  _io = io;
  // Auth middleware on handshake
  io.use(async (socket: any, next) => {
    try {
      const token = socket.handshake.auth?.token || socket.handshake.headers?.authorization?.split(' ')[1];
      if (!token) return next(new Error('No token'));
      if (await isTokenBlacklisted(token)) return next(new Error('Token revoked'));
      const payload = verifyAccessToken(token);
      socket.userId = payload.sub;
      socket.username = payload.username;
      const user = await prisma.user.findUnique({ 
        where: { id: payload.sub }, 
        select: { displayName: true, roles: { include: { role: true } } } 
      });
      if (!user) {
        return next(new Error('User not found'));
      }
      socket.displayName = user?.displayName ?? payload.username;
      
      let perms: string[] = [];
      if (user?.roles) {
        user.roles.forEach((ur: any) => {
          if (ur.role && ur.role.permissions) {
            perms.push(...(ur.role.permissions as string[]));
          }
        });
      }
      socket.permissions = [...new Set(perms)];

      next();
    } catch {
      next(new Error('Authentication failed'));
    }
  });

  io.on('connection', async (socket: any) => {
    const authSocket = socket as AuthSocket;
    console.log(`🔌 Socket connected: ${authSocket.userId}`);

    // Join personal room
    authSocket.join(`user:${authSocket.userId}`);

    // Restore or set presence
    const existingUser = await prisma.user.findUnique({ where: { id: authSocket.userId }, select: { presence: true } });
    if (!existingUser) {
      console.log(`🔌 Socket connection rejected: User ${authSocket.userId} not found in DB`);
      return authSocket.disconnect(true);
    }

    let newPresence = 'online';
    if (existingUser.presence && existingUser.presence !== 'offline') {
      newPresence = existingUser.presence;
    }
    await setPresence(authSocket.userId, newPresence);
    await prisma.user.update({ where: { id: authSocket.userId }, data: { presence: newPresence as any, lastSeen: new Date() } });
    io.emit(SocketEvents.PRESENCE_UPDATE, { userId: authSocket.userId, presence: newPresence });

    // Auto-join user's channels & conversation rooms
    const [channelMemberships, conversationMemberships, teamMemberships, userRoles] = await Promise.all([
      prisma.channelMember.findMany({ where: { userId: authSocket.userId }, select: { channelId: true } }),
      prisma.conversationMember.findMany({ where: { userId: authSocket.userId }, select: { conversationId: true } }),
      prisma.teamMember.findMany({
        where: { userId: authSocket.userId },
        select: { team: { select: { channels: { where: { isPrivate: false }, select: { id: true } } } } }
      }),
      prisma.userRole.findMany({
        where: { userId: authSocket.userId },
        include: { role: true }
      })
    ]);
    
    // Join admin chat logs room if permitted
    const hasViewChatLogs = userRoles.some(ur => {
      const perms = (ur.role.permissions as string[]) || [];
      return perms.includes('view_chat_logs') || perms.includes('*');
    });
    if (hasViewChatLogs) {
      authSocket.join('admin:chat_logs');
    }
    
    // Join explicit channel memberships
    channelMemberships.forEach(({ channelId }: { channelId: string }) => authSocket.join(`channel:${channelId}`));
    
    // Join all public channels for teams the user is in
    teamMemberships.forEach(tm => {
      tm.team.channels.forEach(ch => authSocket.join(`channel:${ch.id}`));
    });

    conversationMemberships.forEach(({ conversationId }: { conversationId: string }) => authSocket.join(`conversation:${conversationId}`));

    // Register event handlers
    messageHandler(io, authSocket);
    presenceHandler(io, authSocket);
    typingHandler(io, authSocket);
    webrtcHandler(io, authSocket);
    meetingHandler(io, authSocket);

    // Attention (Spark-style buzz) handler
    authSocket.on(SocketEvents.ATTENTION_SEND, async ({ conversationId, emoji }: { conversationId: string; emoji?: string }) => {
      try {
        const conversation = await prisma.conversation.findUnique({
          where: { id: conversationId },
          include: { members: { select: { userId: true } } },
        });
        if (!conversation) return;
        const targetUserId = conversation.members.find((m: any) => m.userId !== authSocket.userId)?.userId;
        if (!targetUserId) return;
        io.to(`user:${targetUserId}`).emit(SocketEvents.ATTENTION_RECEIVED, {
          conversationId,
          fromUserId: authSocket.userId,
          fromDisplayName: authSocket.displayName,
          emoji,
        });
      } catch (err) {
        console.error('attention:send error', err);
      }
    });

    // Manual room join (validated against DB membership to prevent unauthorized access)
    authSocket.on(SocketEvents.ROOM_JOIN, async ({ room }: { room: string }) => {
      try {
        if (room.startsWith('conversation:')) {
          const conversationId = room.replace('conversation:', '');
          const membership = await prisma.conversationMember.findUnique({
            where: { conversationId_userId: { conversationId, userId: authSocket.userId } },
          });
          if (!membership) {
            console.warn(`⛔ Unauthorized ROOM_JOIN attempt: user ${authSocket.userId} -> ${room}`);
            return;
          }
        } else if (room.startsWith('channel:')) {
          const channelId = room.replace('channel:', '');
          const channel = await prisma.channel.findUnique({
            where: { id: channelId },
            include: { 
              members: { where: { userId: authSocket.userId } }, 
              team: { include: { members: { where: { userId: authSocket.userId } } } } 
            }
          });
          if (!channel) return;
          const isMember = channel.members.length > 0;
          const isTeamMember = channel.team.members.length > 0;
          
          if (!isMember && (channel.isPrivate || !isTeamMember)) {
            console.warn(`⛔ Unauthorized ROOM_JOIN attempt: user ${authSocket.userId} -> ${room}`);
            return;
          }
        }
        // Only join if membership is verified
        authSocket.join(room);
      } catch (err) {
        console.error('ROOM_JOIN validation error', err);
      }
    });

    authSocket.on(SocketEvents.ROOM_LEAVE, ({ room }: { room: string }) => {
      authSocket.leave(room);
    });

    authSocket.on('disconnect', async () => {
      console.log(`🔌 Socket disconnected: ${authSocket.userId}`);
      // Check if user has other active connections
      const sockets = await io.in(`user:${authSocket.userId}`).fetchSockets();
      if (sockets.length === 0) {
        await setPresence(authSocket.userId, 'offline');
        await prisma.user.update({ where: { id: authSocket.userId }, data: { presence: 'offline', lastSeen: new Date() } });
        io.emit(SocketEvents.PRESENCE_UPDATE, { userId: authSocket.userId, presence: 'offline' });
      }
    });
  });
}
